import { GenerateImageOptions, GenerateImageRendererOptions, MaterialFace, ModelViewerMaterialPrimitiveType } from "@centricsoftware/model-viewer";
import comm from "csi/comm";
import JsonResponse from "csi/comm/JsonResponse";
import { ImageDataMap } from "csi/middleware/imageTemplates3D/imageGenerateService";
import reflection from "csi/reflection";
import { getFileUploadRequest } from "csi/service/fileService";
import { WriterCallbackFunction, submitWriter } from "csi/service/writerService";
import { OnSaveCallback } from "csi/threeD/snapshotTemplates/dialogs/SnapshotTemplateDialogStore";
import { GenerateImageFormat, GenerateImageType, SnapshotMimeType, SnapshotTemplateId, SnapshotTemplateOptions, imageRatioMap } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { cnl } from "csi/typings";
import { combineHexAlpha } from "csi/utility/colorUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import JSZip from "jszip";
import { capitalize } from "lodash";
import { SnapshotDataMap } from "./snapshotsStore";


type TemplateAttributes = {
	isTurntable: boolean;
	imageCount: string;
	typeOfImage: GenerateImageType;
	format: GenerateImageFormat;
	ratio: string;
	backgroundColorWithAlpha: string;
	use3DEnvironmentAsBackground: boolean;
	cameraPositions?: string[];
	baseGeometry?: string[];
	materialSide?: string[];
};

const TEMPLATE_ATTRIBUTES = {
	BackgroundColor: "BackgroundColor",
	ImageCount: "ImageCount",
	IncludeAllVariants: "IncludeAllVariants",
	ImageType: "ImageType",
	ImageRatio: "ImageRatio",
	ImageFormat: "ImageFormat",
	ThreeDEnvironment: "ThreeDEnvironment",
	Description: "Description",
	NodeName: "Node Name",
	StyleType: "StyleTypes",
	MaterialType: "MaterialTypes",
	CameraPosition: "CameraPositions",
	Use3DEnvironmentAsBackground: "Use3DEnvironmentAsBackground",
	BaseGeometry: "BaseGeometry",
	MaterialSide: "MaterialSide",
	Active: "Active",
};

const {
	BackgroundColor,
	ImageCount,
	ImageType,
	ImageRatio,
	ImageFormat,
	ThreeDEnvironment,
	CameraPosition,
	Use3DEnvironmentAsBackground,
	BaseGeometry,
	MaterialSide,
	NodeName,
	Description,
	StyleType,
	IncludeAllVariants,
	MaterialType,
	Active,
} = TEMPLATE_ATTRIBUTES;

/** make request to Upload image files to given url locations */
async function uploadImageFiles (urls: cnl[], fileData: File[]): Promise<void> {
	if (!fileData?.length) {
		return;
	}

	await Promise.all(urls.map(async (url) => {
		// This request will need to be conditional based on the the fileData being part of a turntable or not
		const request = getFileUploadRequest(fileData, null, url, "Images", "Files", false, false, true, "SiteAdmin", "MultiFilePublishWithImages");
		await request.go();
	}));
};

/** Upload camera position screen shot to a camera position node */
export async function uploadCameraPositionPreview(blob: Blob, imageName: string, url:cnl): Promise<JsonResponse | undefined> {
	if (!blob) {
		return;
	}
	const file =  new File([blob], imageName + ".png");
	const request = getFileUploadRequest([file], null, url, "Preview", null, false, true, true, "SiteAdmin", "MultiFilePublish");
	try {
		return await request.go();
	} catch (error) {
		console.error("Error uploading camera position preview", error);
	}
};

/** Upload tuntable images */
async function uploadTurntableImageFiles (urls: cnl[], fileData: File[]): Promise<void> {
	if (!fileData?.length) {
		return;
	}

	const zip = new JSZip();
	fileData.forEach((file) => {
		zip.file(file.name, file);
	});
	const zipBlob = await zip.generateAsync({ type: "blob" });
	const zipFile = new File([zipBlob], `turntableImages_${Date.now()}.zip`);
	await Promise.all(urls.map(async (url) => {
		const request = getFileUploadRequest([zipFile], null, url, "Images", null, false, true, true, "SiteAdmin", "MultiFilePublish");
		request.addParameter("Turntable", true);
		request.addParameter("NewNodeTypes", reflection.TYPE_IMAGE );
		await request.go();
	}));
};

/** General function to upload snapshots and generated images */
export async function uploadSnapshots(selectedSnapshots: SnapshotDataMap[] | ImageDataMap[], selectedSnapshotSaveLocationsUrls: cnl[]): Promise<void> {
	const selectedSnapshotsExcludingTurntables = selectedSnapshots.filter((item) => !item.isTurntable);
	const selectedSnapshotsTurntables = selectedSnapshots.filter((item) => item.isTurntable);
	try {
		if (selectedSnapshotsExcludingTurntables.length) {
			const fileData = selectedSnapshotsExcludingTurntables.map((item) => item.file);
			await uploadImageFiles(selectedSnapshotSaveLocationsUrls, fileData);
		}
	} catch (error) {
		throw error;
	} finally {
		try {
			if (selectedSnapshotsTurntables.length) {
				for (const item of selectedSnapshotsTurntables) {
					const { turntableFiles } = item;
					await uploadTurntableImageFiles(selectedSnapshotSaveLocationsUrls, turntableFiles);
				}
			}
		} catch (error) {
			throw error;
		}
	}
};

/** Update changes to a template */
export async function saveTemplateChanges(
	templateId: cnl,
	imageOptions:GenerateImageOptions,
	rendererOptions: GenerateImageRendererOptions,
	templateOptions: SnapshotTemplateOptions,
	snapshotTemplateId: SnapshotTemplateId,
	onSave?: WriterCallbackFunction,
): Promise<JsonResponse> {
	const { includeAllVariants, environmentURL, templateDescription, templateName, productTypes } = templateOptions;
	const {
		imageCount,
		typeOfImage,
		format,
		ratio,
		backgroundColorWithAlpha,
		cameraPositions,
		use3DEnvironmentAsBackground,
		baseGeometry,
		materialSide,
	} = generateImageAttributes(imageOptions, rendererOptions);
	const productTypeValues = productTypes.map((item) => item.value);

	const writer = new Writer();
	const cn = writer.changeNode(templateId)
		.changeAttribute(NodeName, xml.ATTR_STRING, templateName)
		.changeAttribute(Description, xml.ATTR_STRING, templateDescription)
		.changeAttribute(BackgroundColor, xml.ATTR_STRING, backgroundColorWithAlpha)
		.changeAttribute(ImageCount, xml.ATTR_ENUM, xml.enumCreate("ImageCount", imageCount))
		.changeAttribute(ImageType, xml.ATTR_ENUM, xml.enumCreate("ImageType", typeOfImage))
		.changeAttribute(ImageRatio, xml.ATTR_ENUM, xml.enumCreate("AspectRatio", ratio))
		.changeAttribute(ImageFormat, xml.ATTR_ENUM, xml.enumCreate("ImageFormat", format))
		.changeAttribute(ThreeDEnvironment, xml.ATTR_REF, environmentURL)
		.changeAttribute(CameraPosition, xml.ATTR_REFLIST, cameraPositions)
		.changeAttribute(Use3DEnvironmentAsBackground, xml.ATTR_BOOLEAN, use3DEnvironmentAsBackground);
	if (snapshotTemplateId === SnapshotTemplateId.Material){
		cn.changeAttribute(BaseGeometry, xml.ATTR_ENUMLIST, baseGeometry)
			.changeAttribute(MaterialSide, xml.ATTR_ENUMLIST, materialSide)
			.changeAttribute(MaterialType, xml.ATTR_REFLIST, productTypeValues);
	} else {
		cn.changeAttribute(IncludeAllVariants, xml.ATTR_BOOLEAN, includeAllVariants)
			.changeAttribute(StyleType, xml.ATTR_REFLIST, productTypeValues);
	}

	writer.validateNode(templateId, true);
	const response = await submitWriter(writer);
	onSave?.(response);
	return response;
};

/** Create a new template */
export async function saveNewTemplate(
	activeFlag: boolean,
	snapshotTemplateId: SnapshotTemplateId,
	imageOptions: GenerateImageOptions,
	rendererOptions: GenerateImageRendererOptions,
	templateOptions: SnapshotTemplateOptions,
	writer: Writer,
	onSave: OnSaveCallback,
): Promise<JsonResponse> {
	const { templateName, templateDescription, includeAllVariants, productTypes, environmentURL } = templateOptions;
	const {
		imageCount,
		typeOfImage,
		format,
		ratio,
		backgroundColorWithAlpha,
		cameraPositions,
		use3DEnvironmentAsBackground,
		baseGeometry,
		materialSide,
	} = generateImageAttributes(imageOptions, rendererOptions);
	const productTypeValues = productTypes.map((item) => item.value);

	const nodeToCreate = snapshotTemplateId === SnapshotTemplateId.Material ? "MaterialSnapshotTemplate" : "StyleSnapshotTemplate";
	const newURL = comm.createUrl();
	const cn = writer.createNode(newURL, nodeToCreate)
		.changeAttribute(NodeName, xml.ATTR_STRING, templateName)
		.changeAttribute(Description, xml.ATTR_STRING, templateDescription)
		.changeAttribute(BackgroundColor, xml.ATTR_STRING, backgroundColorWithAlpha)
		.changeAttribute(ImageCount, xml.ATTR_ENUM, xml.enumCreate("ImageCount", imageCount))
		.changeAttribute(ImageType, xml.ATTR_ENUM, xml.enumCreate("ImageType", typeOfImage))
		.changeAttribute(ImageRatio, xml.ATTR_ENUM, xml.enumCreate("AspectRatio", ratio))
		.changeAttribute(ImageFormat, xml.ATTR_ENUM, xml.enumCreate("ImageFormat", format))
		.changeAttribute(ThreeDEnvironment, xml.ATTR_REF, environmentURL)
		.changeAttribute(CameraPosition, xml.ATTR_REFLIST, cameraPositions)
		.changeAttribute(Use3DEnvironmentAsBackground, xml.ATTR_BOOLEAN, use3DEnvironmentAsBackground)
		.changeAttribute(Active, xml.ATTR_BOOLEAN, activeFlag);

	if (nodeToCreate === "StyleSnapshotTemplate") {
		cn.changeAttribute(StyleType, xml.ATTR_REFLIST, productTypeValues)
			.changeAttribute(IncludeAllVariants, xml.ATTR_BOOLEAN, includeAllVariants);
	} else {
		cn.changeAttribute(MaterialType, xml.ATTR_REFLIST, productTypeValues)
			.changeAttribute(BaseGeometry, xml.ATTR_ENUMLIST, baseGeometry)
			.changeAttribute(MaterialSide, xml.ATTR_ENUMLIST, materialSide);
	}

	writer.validateNode(newURL, true);
	const response = await submitWriter(writer);
	onSave(response, newURL);
	return response;
};

/** Utility to gather values for change attributes */
function generateImageAttributes(imageOptions: GenerateImageOptions, rendererOptions: GenerateImageRendererOptions): TemplateAttributes {
	const { imageType, imageRatio, background, backgroundIntensity, environment } = rendererOptions;

	const isTurntable = imageOptions.camera.turntable !== undefined;
	const imageCount = String(isTurntable ? imageOptions.camera.turntable?.numberOfImages : "");
	const typeOfImage = isTurntable ? GenerateImageType.Turntable : GenerateImageType.Image;
	const format = imageType === SnapshotMimeType.JPEG ? GenerateImageFormat.JPEG : GenerateImageFormat.PNG;
	const ratio = Object.entries(imageRatioMap).find(([key, value]) => value === imageRatio)[0];
	const backgroundColorWithAlpha = environment === background ? "#FFFFFFFF" : combineHexAlpha(background as string, backgroundIntensity);
	const cameraPositions = isTurntable ? [] : imageOptions.camera.cameraPositions.map((item) => item.id);
	const use3DEnvironmentAsBackground = environment === background;

	const materialOptions = imageOptions.materials;
	const baseGeometry = mapGeometryEnumValues(materialOptions.primitives);
	const materialSide = mapMaterialSideEnumValues(materialOptions.sides);
	return {
		isTurntable,
		imageCount,
		typeOfImage,
		format,
		ratio,
		backgroundColorWithAlpha,
		use3DEnvironmentAsBackground,
		cameraPositions,
		baseGeometry,
		materialSide,
	};
}

function mapGeometryEnumValues(primitiveOptions:ModelViewerMaterialPrimitiveType[] ): string[] {
	const baseGeometry = primitiveOptions.map((item) => {
		switch (item) {
			case "MaterialPrimitiveCube":
				return "Cube";
			case "MaterialPrimitiveSphere":
				return "Sphere";
			case "MaterialPrimitiveFabric":
				return "Fabric";
		}
	});
	return baseGeometry.map((item) => xml.enumCreate("BaseGeometry", item));
}

function mapMaterialSideEnumValues(materialOptions:MaterialFace[]): string[] {
	return materialOptions.map((item) => xml.enumCreate("MaterialSide", capitalize(item)));
}
